
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from '../home/components/Header';
import Footer from '../home/components/Footer';

export default function Microsoft365Page() {
  const location = useLocation();
  const isNL = location.pathname.startsWith('/nl');
  const prefix = isNL ? '/nl' : '';

  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const plans = [
    {
      id: 1,
      name: 'Microsoft 365 Business Basic',
      description: isNL
        ? 'Web- en mobiele versies van Office-apps met zakelijke e-mail en cloudopslag'
        : 'Web and mobile versions of Office apps with business email and cloud storage',
      features: isNL
        ? [
            'Web- en mobiele versies van Word, Excel, PowerPoint en Outlook',
            'Zakelijke e-mailhosting met 50 GB mailbox',
            '1 TB cloudopslag per gebruiker',
            'Microsoft Teams voor chat, bellen en vergaderingen',
            'Standaard beveiligingsfuncties',
          ]
        : [
            'Web and mobile versions of Word, Excel, PowerPoint, and Outlook',
            'Business email hosting with 50 GB mailbox',
            '1 TB of cloud storage per user',
            'Microsoft Teams for chat, calls, and meetings',
            'Standard security features',
          ],
      userRange: isNL ? '1-300 gebruikers' : '1-300 users',
      billing: isNL ? 'Maandelijks of Jaarlijks' : 'Monthly or Annual',
    },
    {
      id: 2,
      name: 'Microsoft 365 Business Standard',
      description: isNL
        ? 'Desktopversies van Office-apps plus alle Business Basic-functies'
        : 'Desktop versions of Office apps plus all Business Basic features',
      features: isNL
        ? [
            'Desktopversies van Word, Excel, PowerPoint, Outlook en meer',
            'Alle Business Basic-functies inbegrepen',
            'Webinarhosting voor maximaal 300 deelnemers',
            'Deelnemerregistratie en rapportagetools',
            'Klantafspraakplanning',
            'Verbeterde beveiliging en apparaatbeheer',
          ]
        : [
            'Desktop versions of Word, Excel, PowerPoint, Outlook, and more',
            'All Business Basic features included',
            'Webinar hosting for up to 300 attendees',
            'Attendee registration and reporting tools',
            'Customer appointment booking',
            'Enhanced security and device management',
          ],
      userRange: isNL ? '1-300 gebruikers' : '1-300 users',
      billing: isNL ? 'Maandelijks of Jaarlijks' : 'Monthly or Annual',
    },
    {
      id: 3,
      name: 'Microsoft 365 Business Premium',
      description: isNL
        ? 'Geavanceerde beveiliging, apparaatbeheer en informatiebescherming'
        : 'Advanced security, device management, and information protection',
      features: isNL
        ? [
            'Alle Business Standard-functies inbegrepen',
            'Geavanceerde dreigingsbescherming',
            'Informatiebescherming en compliancetools',
            'Azure Information Protection',
            'Intune-apparaatbeheer',
            'Windows 10/11 Business-licenties',
          ]
        : [
            'All Business Standard features included',
            'Advanced threat protection',
            'Information protection and compliance tools',
            'Azure Information Protection',
            'Intune device management',
            'Windows 10/11 Business licenses',
          ],
      userRange: isNL ? '1-300 gebruikers' : '1-300 users',
      billing: isNL ? 'Maandelijks of Jaarlijks' : 'Monthly or Annual',
    },
    {
      id: 4,
      name: 'Microsoft 365 Apps for Business',
      description: isNL
        ? 'Alleen desktop Office-apps, zonder e-mail of cloudopslag'
        : 'Desktop Office apps only, no email or cloud storage',
      features: isNL
        ? [
            'Desktopversies van Word, Excel, PowerPoint, Outlook en meer',
            'Publisher en Access (alleen PC)',
            '1 TB OneDrive-cloudopslag per gebruiker',
            'Installeren op maximaal 5 apparaten per gebruiker',
            'Geen e-mailhosting inbegrepen',
          ]
        : [
            'Desktop versions of Word, Excel, PowerPoint, Outlook, and more',
            'Publisher and Access (PC only)',
            '1 TB OneDrive cloud storage per user',
            'Install on up to 5 devices per user',
            'No email hosting included',
          ],
      userRange: isNL ? '1-300 gebruikers' : '1-300 users',
      billing: isNL ? 'Maandelijks of Jaarlijks' : 'Monthly or Annual',
    },
    {
      id: 5,
      name: 'Microsoft 365 E3',
      description: isNL
        ? 'Enterprise-productiviteit en beveiliging voor grote organisaties'
        : 'Enterprise-grade productivity and security for large organizations',
      features: isNL
        ? [
            'Alle desktop- en web-Office-applicaties',
            'Onbeperkte e-mailopslag',
            'Geavanceerde compliance en informatiebescherming',
            'Geavanceerde analyses met Power BI Pro',
            'Audiovergaderingen',
            'Beveiligingsfuncties op enterprise-niveau',
          ]
        : [
            'All desktop and web Office applications',
            'Unlimited email storage',
            'Advanced compliance and information protection',
            'Advanced analytics with Power BI Pro',
            'Audio conferencing',
            'Enterprise-level security features',
          ],
      userRange: isNL ? 'Onbeperkt aantal gebruikers' : 'Unlimited users',
      billing: isNL ? 'Maandelijks of Jaarlijks' : 'Monthly or Annual',
    },
    {
      id: 6,
      name: 'Microsoft 365 E5',
      description: isNL
        ? 'Premium enterprise-oplossing met geavanceerde beveiliging en analyses'
        : 'Premium enterprise solution with advanced security and analytics',
      features: isNL
        ? [
            'Alle Microsoft 365 E3-functies inbegrepen',
            'Geavanceerde dreigingsbescherming en beveiliging',
            'Geavanceerde compliance en eDiscovery',
            'Telefoonsysteem met belmogelijkheden',
            'Geavanceerde analyses en BI',
            'MyAnalytics persoonlijke productiviteitsinzichten',
          ]
        : [
            'All Microsoft 365 E3 features included',
            'Advanced threat protection and security',
            'Advanced compliance and eDiscovery',
            'Phone System with calling capabilities',
            'Advanced analytics and BI',
            'MyAnalytics personal productivity insights',
          ],
      userRange: isNL ? 'Onbeperkt aantal gebruikers' : 'Unlimited users',
      billing: isNL ? 'Maandelijks of Jaarlijks' : 'Monthly or Annual',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-orange-50 via-white to-amber-50 pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {isNL ? 'Microsoft 365 Oplossingen' : 'Microsoft 365 Solutions'}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {isNL
                ? 'Versterk uw bedrijf met uitgebreide cloudproductiviteits- en samenwerkingstools. Kies het juiste Microsoft 365-abonnement voor uw organisatie.'
                : 'Empower your business with comprehensive cloud productivity and collaboration tools. Choose the right Microsoft 365 plan for your organization.'}
            </p>
            <div className="flex justify-center gap-4">
              <Link
                to={`${prefix}/contact`}
                className="px-8 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
              </Link>
              <a
                href="#plans"
                className="px-8 py-3 border-2 border-orange-500 text-orange-500 rounded-lg font-medium hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                {isNL ? 'Bekijk Abonnementen' : 'View Plans'}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {isNL ? 'Waarom Kiezen voor Microsoft 365?' : 'Why Choose Microsoft 365?'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {isNL
                ? 'Transformeer de manier waarop uw team werkt met geïntegreerde apps en intelligente clouddiensten'
                : 'Transform the way your team works with integrated apps and intelligent cloud services'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-xl">
              <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-cloud-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {isNL ? 'Werk Overal' : 'Work from Anywhere'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Krijg toegang tot uw bestanden en applicaties vanaf elk apparaat, overal ter wereld met cloudgebaseerde productiviteitstools.'
                  : 'Access your files and applications from any device, anywhere in the world with cloud-based productivity tools.'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-white p-8 rounded-xl">
              <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {isNL ? 'Enterprise Beveiliging' : 'Enterprise Security'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Bescherm uw bedrijf met geavanceerde beveiligingsfuncties, dreigingsbescherming en compliancetools die in elk abonnement zijn ingebouwd.'
                  : 'Protect your business with advanced security features, threat protection, and compliance tools built into every plan.'}
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-xl">
              <div className="w-14 h-14 bg-orange-500 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-2xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {isNL ? 'Naadloze Samenwerking' : 'Seamless Collaboration'}
              </h3>
              <p className="text-gray-600">
                {isNL
                  ? 'Maak realtime samenwerking mogelijk met Microsoft Teams, SharePoint en co-auteursmogelijkheden in alle Office-apps.'
                  : 'Enable real-time collaboration with Microsoft Teams, SharePoint, and co-authoring capabilities across all Office apps.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section */}
      <section id="plans" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {isNL ? 'Microsoft 365 Abonnementen' : 'Microsoft 365 Plans'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {isNL
                ? 'Vind het perfecte abonnement voor uw zakelijke behoeften. Alle abonnementen bevatten regelmatige updates en 24/7 ondersteuning.'
                : 'Find the perfect plan for your business needs. All plans include regular updates and 24/7 support.'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden ${
                  selectedPlan === plan.name ? 'ring-2 ring-orange-500' : ''
                }`}
              >
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-6 min-h-[48px]">{plan.description}</p>

                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                      <i className="ri-user-line"></i>
                      <span>{plan.userRange}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <i className="ri-calendar-line"></i>
                      <span>{plan.billing}</span>
                    </div>
                  </div>

                  <div className="mb-8">
                    <h4 className="font-semibold text-gray-900 mb-4">
                      {isNL ? 'Belangrijkste Functies:' : 'Key Features:'}
                    </h4>
                    <ul className="space-y-3">
                      {plan.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <i className="ri-check-line text-orange-500 text-lg mt-0.5 flex-shrink-0"></i>
                          <span className="text-sm text-gray-600">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <Link
                    to={`${prefix}/contact`}
                    onClick={() => setSelectedPlan(plan.name)}
                    className="block w-full px-6 py-3 bg-orange-500 text-white text-center rounded-lg font-medium hover:bg-orange-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {isNL ? 'Offerte Aanvragen' : 'Request a Quote'}
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {isNL ? 'Aanvullende Diensten' : 'Additional Services'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {isNL
                ? 'Haal het maximale uit uw Microsoft 365-investering met onze expertdiensten'
                : 'Maximize your Microsoft 365 investment with our expert services'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-settings-3-line text-xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {isNL ? 'Migratiediensten' : 'Migration Services'}
              </h3>
              <p className="text-sm text-gray-600">
                {isNL
                  ? 'Naadloze migratie van verouderde systemen naar Microsoft 365 met minimale downtime.'
                  : 'Seamless migration from legacy systems to Microsoft 365 with minimal downtime.'}
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-graduation-cap-line text-xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {isNL ? 'Training & Adoptie' : 'Training & Adoption'}
              </h3>
              <p className="text-sm text-gray-600">
                {isNL
                  ? "Uitgebreide trainingsprogramma's om uw team te helpen de productiviteit te maximaliseren."
                  : 'Comprehensive training programs to help your team maximize productivity.'}
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {isNL ? 'Beveiligingsconfiguratie' : 'Security Setup'}
              </h3>
              <p className="text-sm text-gray-600">
                {isNL
                  ? 'Configureer geavanceerde beveiligingsfuncties en compliancebeleid voor uw organisatie.'
                  : 'Configure advanced security features and compliance policies for your organization.'}
              </p>
            </div>

            <div className="bg-gray-50 p-6 rounded-xl hover:shadow-md transition-shadow cursor-pointer">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-customer-service-2-line text-xl text-orange-500"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {isNL ? 'Doorlopende Ondersteuning' : 'Ongoing Support'}
              </h3>
              <p className="text-sm text-gray-600">
                {isNL
                  ? '24/7 technische ondersteuning en beheer voor uw Microsoft 365-omgeving.'
                  : '24/7 technical support and management for your Microsoft 365 environment.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-500 to-amber-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {isNL ? 'Klaar om Uw Bedrijf te Transformeren?' : 'Ready to Transform Your Business?'}
          </h2>
          <p className="text-xl text-white/90 mb-8">
            {isNL
              ? 'Laat onze experts u helpen bij het kiezen en implementeren van de juiste Microsoft 365-oplossing voor uw organisatie.'
              : 'Let our experts help you choose and implement the right Microsoft 365 solution for your organization.'}
          </p>
          <div className="flex justify-center gap-4">
            <Link
              to={`${prefix}/contact`}
              className="px-8 py-3 bg-white text-orange-500 rounded-lg font-medium hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              {isNL ? 'Begin Vandaag' : 'Get Started Today'}
            </Link>
            <Link
              to={`${prefix}/support`}
              className="px-8 py-3 border-2 border-white text-white rounded-lg font-medium hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              {isNL ? 'Neem Contact Op' : 'Contact Support'}
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
